import { EventResultItem, ResultsSectionProps } from '@common/types/search';
import { EventResultCard } from '@components/display/search/EventResultsSection/EventResultCard';
import { Button } from '@components/utility/buttons';
import { Divider, Grid2, Skeleton, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

const NEXT_RESULTS_STEP = 20;

export const EventResultsSection = <T extends EventResultItem>({
    Icon,
    name,
    search,
    results,
    totalResultsCount,
    onShowNext,
    loading,
    hitsPerPage = 4,
}: ResultsSectionProps<T>) => {
    const displayedCount = results.length;
    const totalCount = totalResultsCount ?? displayedCount;
    const remainingCount = Math.max(totalCount - displayedCount, 0);
    const nextCount = Math.min(remainingCount, NEXT_RESULTS_STEP);
    const intl = useIntl();

    return (
        <section>
            <Stack direction="row" alignItems="center" gap="6px" mb="8px">
                <Icon width={16} height={16} sx={{ fontSize: 16 }} />
                <Typography fontSize={14} letterSpacing={0} lineHeight="21px">
                    {loading ? (
                        <Skeleton variant="text" width={100} />
                    ) : totalCount > 0 ? (
                        `${totalCount} ${name}`
                    ) : (
                        intl.formatMessage(
                            { id: 'search.results.notFound', defaultMessage: 'No {name} found' },
                            { name: name.toLowerCase() }
                        )
                    )}
                </Typography>
            </Stack>
            <Divider />
            {loading ? (
                <Grid2 container my="16px" spacing="15px">
                    {results.map((_, index) => (
                        <Grid2
                            key={`skeleton-${index}`}
                            size={{ xs: 12, md: Math.floor(12 / Math.max(Math.min(hitsPerPage, 4), 1)) }}
                        >
                            <EventResultCard
                                loading
                                data={{
                                    title: '',
                                    url: { href: '' },
                                }}
                                term={search}
                            />
                        </Grid2>
                    ))}
                </Grid2>
            ) : (
                totalCount > 0 && (
                    <Grid2 container my="16px" spacing="15px">
                        {results.map((result, index) => (
                            <Grid2
                                key={`${name}-result-${index}`}
                                size={{ xs: 12, md: Math.floor(12 / Math.max(Math.min(hitsPerPage, 4), 1)) }}
                            >
                                <EventResultCard key={`${name}-result-${index}`} data={result} term={search} />
                            </Grid2>
                        ))}
                    </Grid2>
                )
            )}
            {loading ? (
                <Skeleton variant="rounded" className="rounded-full" width={200} height={26} />
            ) : (
                remainingCount > 0 && (
                    <Button.Neutral onClick={() => onShowNext?.(displayedCount + nextCount)}>
                        {intl.formatMessage(
                            { id: 'search.results.showNext', defaultMessage: 'Show next {count} {name} results' },
                            { count: nextCount, name: name.toLowerCase() }
                        )}
                    </Button.Neutral>
                )
            )}
        </section>
    );
};
